import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Check } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useZoneNew } from '../hooks/useZoneNew';

const COLORS = [
  'bg-emerald-600',
  'bg-blue-600',
  'bg-purple-600',
  'bg-yellow-500',
  'bg-pink-500',
  'bg-teal-500',
  'bg-slate-400',
];

interface ZoneInfoFormProps {
  formData: any;
  handleChange: (field: string, value: any) => void;
  projects: any[];
  units: any[];
  parentZones: any[];
  isLoadingProjects: boolean;
  isLoadingUnits: boolean;
}

export function ZoneInfoForm({
  formData,
  handleChange,
  projects,
  units,
  parentZones,
  isLoadingProjects,
  isLoadingUnits
}: ZoneInfoFormProps) {
  return (
    <div className="space-y-6">
      {/* Thông tin khu vực */}
      <Card className="p-6 space-y-6 border-slate-100">
        <h3 className="text-sm font-normal text-slate-900">Thông tin khu vực</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="sm:text-sm text-xs font-normal text-slate-700">Tên khu vực <span className="text-red-500">*</span></label>
              <span className="text-[11px] text-slate-400">0/100</span>
            </div>
            <Input 
              placeholder="Nhập tên khu vực" 
              value={formData.zone_name}
              onChange={(e) => handleChange('zone_name', e.target.value)}
              className="h-10 sm:text-sm text-xs" 
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="sm:text-sm text-xs font-normal text-slate-700">Mã khu vực <span className="text-red-500">*</span></label>
              <span className="text-[11px] text-slate-400">0/50</span>
            </div>
            <Input 
              placeholder="Tự động tạo nếu để trống" 
              value={formData.zone_code}
              onChange={(e) => handleChange('zone_code', e.target.value)}
              className="h-10 sm:text-sm text-xs" 
            />
          </div>

          <div className="space-y-1.5">
            <label className="sm:text-sm text-xs font-normal text-slate-700">Khu vực cha</label>
            <Select 
              value={formData.parent_zone_id} 
              onValueChange={(v) => handleChange('parent_zone_id', v)} 
              options={[
                { value: '', label: 'Chọn khu vực cha (nếu có)' },
                ...parentZones.map(z => ({ value: z.id, label: z.zone_name }))
              ]} 
              className="h-10 sm:text-sm text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <label className="sm:text-sm text-xs font-normal text-slate-700">Dự án / Công việc <span className="text-red-500">*</span></label>
            <Select 
              value={formData.project_id} 
              onValueChange={(v) => handleChange('project_id', v)} 
              options={[
                { value: '', label: isLoadingProjects ? 'Đang tải dự án...' : 'Chọn dự án hoặc công việc' },
                ...projects.map(p => ({ value: p.id, label: `${p.code} - ${p.name}` }))
              ]} 
              className="h-10 sm:text-sm text-xs"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="sm:text-sm text-xs font-normal text-slate-700">Loại khu vực <span className="text-red-500">*</span></label>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => handleChange('zone_type', 'construction')}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-md border sm:text-sm text-xs transition-all w-[180px]",
                formData.zone_type === 'construction' 
                  ? "border-emerald-600 bg-emerald-50/50 text-emerald-600 font-normal" 
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              )}
            >
              <div className={cn("w-4 h-4 rounded-full flex items-center justify-center border", formData.zone_type === 'construction' ? "border-emerald-600" : "border-slate-300")}>
                {formData.zone_type === 'construction' && <div className="w-2 h-2 rounded-full bg-emerald-600" />}
              </div>
              Thi công
            </button>
            <button
              type="button"
              onClick={() => handleChange('zone_type', 'maintenance')}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-md border sm:text-sm text-xs transition-all w-[180px]",
                formData.zone_type === 'maintenance' 
                  ? "border-emerald-600 bg-emerald-50/50 text-emerald-600 font-normal" 
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              )}
            >
              <div className={cn("w-4 h-4 rounded-full flex items-center justify-center border", formData.zone_type === 'maintenance' ? "border-emerald-600" : "border-slate-300")}>
                {formData.zone_type === 'maintenance' && <div className="w-2 h-2 rounded-full bg-emerald-600" />}
              </div>
              Bảo dưỡng
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="sm:text-sm text-xs font-normal text-slate-700">Mô tả</label>
            <span className="text-[11px] text-slate-400">0/300</span>
          </div>
          <textarea 
            placeholder="Nhập mô tả khu vực (không bắt buộc)" 
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="w-full h-24 rounded-md border border-slate-200 p-3 sm:text-sm text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all resize-none"
          />
        </div>
      </Card>

      {/* Thông số khu vực */}
      <Card className="p-6 space-y-6 border-slate-100">
        <h3 className="text-sm font-normal text-slate-900">Thông số khu vực</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-1.5">
            <label className="sm:text-sm text-xs font-normal text-slate-700">Diện tích (m²)</label>
            <div className="relative">
              <Input 
                placeholder="Nhập diện tích" 
                type="number"
                value={formData.area_m2}
                onChange={(e) => handleChange('area_m2', e.target.value)}
                className="h-10 sm:text-sm text-xs pr-8" 
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">m²</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="sm:text-sm text-xs font-normal text-slate-700">Chu vi (m)</label>
            <div className="relative">
              <Input 
                placeholder="Nhập chu vi" 
                type="number"
                value={formData.perimeter_m}
                onChange={(e) => handleChange('perimeter_m', e.target.value)}
                className="h-10 sm:text-sm text-xs pr-8" 
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">m</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="sm:text-sm text-xs font-normal text-slate-700">Độ cao trung bình (m)</label>
            <div className="relative">
              <Input 
                placeholder="Nhập độ cao" 
                type="number"
                value={formData.avg_height_m}
                onChange={(e) => handleChange('avg_height_m', e.target.value)}
                className="h-10 sm:text-sm text-xs pr-8" 
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">m</span>
            </div>
          </div>
        </div>

        <div className="space-y-1.5 w-[200px]">
          <label className="sm:text-sm text-xs font-normal text-slate-700">Đơn vị tính mặc định</label>
          <Select 
            value={formData.area_unit} 
            onValueChange={(v) => handleChange('area_unit', v)} 
            options={[
              { value: '', label: isLoadingUnits ? 'Đang tải đơn vị...' : 'Chọn đơn vị tính' },
              ...units.map(u => ({ value: u.value, label: u.label }))
            ]} 
            className="h-10 sm:text-sm text-xs"
          />
        </div>
      </Card>

      {/* Thiết lập khác */}
      <Card className="p-6 space-y-6 border-slate-100">
        <h3 className="text-sm font-normal text-slate-900">Thiết lập khác</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <label className="sm:text-sm text-xs font-normal text-slate-700">Màu hiển thị</label>
            <div className="flex items-center gap-3">
              {COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleChange('color_code', color)}
                  className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center transition-transform hover:scale-110",
                    color,
                    formData.color_code === color ? "ring-2 ring-offset-2 ring-emerald-500" : ""
                  )}
                >
                  {formData.color_code === color && <Check className="w-3.5 h-3.5 text-white" />}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="sm:text-sm text-xs font-normal text-slate-700">Trạng thái</label>
            <div className="w-[180px]">
              <Select 
                value={formData.status_code} 
                onValueChange={(v) => handleChange('status_code', v)} 
                options={[
                  { value: 'active', label: 'Hoạt động' },
                  { value: 'paused', label: 'Tạm dừng' },
                  { value: 'completed', label: 'Đã hoàn thành' }
                ]} 
                className="h-10 sm:text-sm text-xs text-emerald-600 bg-emerald-50/50 border-emerald-100"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4 pt-4 border-t border-slate-50">
          <div className="flex items-start gap-3">
            <Switch 
              checked={formData.is_active} 
              onCheckedChange={(v) => handleChange('is_active', v)} 
            />
            <div>
              <p className="sm:text-sm text-xs text-slate-900 font-normal">Cho phép sử dụng</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Khu vực sẽ được sử dụng trong thi công và bảo dưỡng</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Switch 
              checked={formData.is_sensitive} 
              onCheckedChange={(v) => handleChange('is_sensitive', v)} 
            />
            <div>
              <p className="sm:text-sm text-xs text-slate-900 font-normal">Khu vực nhạy cảm</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Yêu cầu phê duyệt khi thực hiện công việc</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
